/**
 * Taschengeld (E20) auf dem Gerät. Buchungen sind gewöhnliche Sync-Zeilen
 * (`pocket_money`): lokal zuerst, dann in die Outbox. Wer was schreiben darf,
 * prüft der Server; Taschengeld nach Regel bucht nur er.
 */
import { newId } from '@kraftwerk/shared';
import type { AllowanceInterval } from '@kraftwerk/shared';
import { apiFetch } from '../api.js';
import { currentOwnerId } from '../auth.js';
import { db } from './local.js';
import type { KraftwerkDatabase, LocalPocketGoal, LocalPocketMoney } from './local.js';
import { stamp, writeRow } from './write.js';

/** Buchungen eines Kindes — neueste zuerst. */
export async function pocketEntries(
  childId: string,
  database: KraftwerkDatabase = db,
): Promise<LocalPocketMoney[]> {
  return (await database.pocket_money.where('childId').equals(childId).toArray())
    .filter((entry) => !entry.deletedAt)
    .sort((a, b) =>
      a.date === b.date ? b.updatedAt.localeCompare(a.updatedAt) : b.date.localeCompare(a.date),
    );
}

/**
 * Wer eingetragen hat, steht sofort auch lokal an der Zeile. Der Abgleich
 * bringt die eigene Zeile nicht zurück — ohne das fehlte das × zum
 * Zurücknehmen dauerhaft (bis 24.10.2026 bei jedem eigenen Eintrag).
 */
function withAuthor(): { userId?: string } {
  const me = currentOwnerId();
  return me ? { userId: me } : {};
}

/** Sparziele eines Kindes — älteste zuerst, so wie sie angelegt wurden. */
export async function pocketGoals(
  childId: string,
  database: KraftwerkDatabase = db,
): Promise<LocalPocketGoal[]> {
  return (await database.pocket_goals.where('childId').equals(childId).toArray())
    .filter((goal) => !goal.deletedAt)
    .sort((a, b) => a.updatedAt.localeCompare(b.updatedAt) || a.id.localeCompare(b.id));
}

export async function addPocketGoal(
  input: { childId: string; householdId: string; name: string; targetCents: number },
  database: KraftwerkDatabase = db,
): Promise<LocalPocketGoal> {
  const row: LocalPocketGoal = {
    id: newId(),
    ...withAuthor(),
    ...input,
    updatedAt: stamp(),
    deletedAt: null,
  };
  await writeRow('pocket_goals', row, 'upsert', database);
  return row;
}

/**
 * Ziel löschen. Das Gesparte kehrt von selbst ins freie Geld zurück
 * (`pocketSummary` zählt Buchungen ohne lebendes Ziel dorthin).
 */
export async function deletePocketGoal(
  goal: LocalPocketGoal,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const now = stamp();
  await writeRow('pocket_goals', { ...goal, updatedAt: now, deletedAt: now }, 'delete', database);
}

/**
 * Eine Ausgabe (das Kind) oder etwas dazu (Erwachsene) — mit `goalId` direkt
 * ins bzw. aus dem Sparziel; `save`/`withdraw` schieben zwischen freiem Geld
 * und Ziel.
 */
export async function addPocketEntry(
  input: {
    childId: string;
    householdId: string;
    kind: 'expense' | 'gift' | 'save' | 'withdraw';
    amountCents: number;
    goalId?: string | null;
    date: string;
    note: string | null;
  },
  database: KraftwerkDatabase = db,
): Promise<LocalPocketMoney> {
  const now = stamp();
  const row: LocalPocketMoney = {
    id: newId(),
    ...withAuthor(),
    ...input,
    goalId: input.goalId ?? null,
    updatedAt: now,
    deletedAt: null,
  };
  await writeRow('pocket_money', row, 'upsert', database);
  return row;
}

/** Eigenen Eintrag zurücknehmen — weich gelöscht, damit es jedes Gerät erfährt. */
export async function deletePocketEntry(
  entry: LocalPocketMoney,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const now = stamp();
  await writeRow('pocket_money', { ...entry, updatedAt: now, deletedAt: now }, 'delete', database);
}

/** Regel setzen oder beenden — schreibt nur der Server, wie jede Mitgliedschaft. */
export async function setAllowance(
  childId: string,
  rule: { amountCents: number; interval: AllowanceInterval; day: number } | null,
): Promise<void> {
  await apiFetch(`/households/children/${encodeURIComponent(childId)}/allowance`, {
    method: 'POST',
    body: JSON.stringify({ rule }),
  });
}

/** Geld für erledigte Aufgaben an/aus — schreibt nur der Server, wie die Regel. */
export async function setTaskRewards(childId: string, enabled: boolean): Promise<void> {
  await apiFetch(`/households/children/${encodeURIComponent(childId)}/rewards`, {
    method: 'POST',
    body: JSON.stringify({ enabled }),
  });
}
